import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  aklioLegalDocuments,
  getAKLIOLegalDocument,
  AKLIOLegalBlock,
} from './aklioLegalDocuments';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500&family=Syne:wght@400;700;800&display=swap');

  .bmx-legal-root *, .bmx-legal-root *::before, .bmx-legal-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .bmx-legal-root {
    --bmx-bg: #f7f9ff;
    --bmx-ink: #101315;
    --bmx-muted: #5f6b7a;
    --bmx-blue: #1a3cff;
    --bmx-cyan: #009dff;
    --bmx-blue-soft: #edf2ff;
    --bmx-amber: #f7c66a;
    --bmx-line: rgba(16, 19, 21, 0.12);
    --bmx-dark: #101315;
    --bmx-mono: 'DM Mono', monospace;
    --bmx-sans: 'Syne', sans-serif;
    min-height: 100vh;
    background: var(--bmx-bg);
    color: var(--bmx-ink);
    font-family: var(--bmx-sans);
  }

  .bmx-legal-nav {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 3rem;
    background: rgba(247, 249, 255, 0.94);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-legal-logo {
    color: var(--bmx-ink);
    font-family: var(--bmx-mono);
    font-size: 0.82rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-decoration: none;
  }

  .bmx-legal-logo span { color: var(--bmx-blue); }

  .bmx-legal-nav-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .bmx-legal-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    padding: 0.65rem 1rem;
    border: 1px solid var(--bmx-blue);
    background: var(--bmx-blue);
    color: #fff;
    cursor: pointer;
    font-family: var(--bmx-mono);
    font-size: 0.74rem;
    letter-spacing: 0.04em;
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;
  }

  .bmx-legal-btn:hover {
    background: #0026cc;
    border-color: #0026cc;
    transform: translateY(-1px);
  }

  .bmx-legal-btn-secondary {
    background: transparent;
    color: var(--bmx-blue);
  }

  .bmx-legal-btn-secondary:hover {
    background: var(--bmx-blue);
    border-color: var(--bmx-blue);
    color: #fff;
  }

  .bmx-legal-hero {
    padding: 5.5rem 3rem 3.5rem;
    background: linear-gradient(135deg, #f7f9ff 0%, #edf2ff 100%);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-legal-eyebrow {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .bmx-legal-eyebrow::before {
    content: '';
    display: inline-block;
    width: 2rem;
    height: 1px;
    background: var(--bmx-blue);
  }

  .bmx-legal-title {
    max-width: 980px;
    margin-top: 1.4rem;
    font-size: clamp(2.8rem, 6vw, 5.4rem);
    font-weight: 800;
    line-height: 0.98;
  }

  .bmx-legal-subtitle {
    max-width: 740px;
    margin-top: 1.2rem;
    color: #3f4b4f;
    font-family: var(--bmx-mono);
    font-size: 0.88rem;
    line-height: 1.9;
  }

  .bmx-legal-date {
    display: inline-flex;
    margin-top: 1.5rem;
    padding: 0.45rem 0.75rem;
    background: #fff;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .bmx-legal-layout {
    display: grid;
    grid-template-columns: 290px minmax(0, 1fr);
    gap: 3rem;
    padding: 3rem;
  }

  .bmx-legal-sidebar {
    position: sticky;
    top: 92px;
    align-self: start;
    display: grid;
    gap: 0.65rem;
  }

  .bmx-legal-sidebar-label {
    margin-bottom: 0.65rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.68rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .bmx-legal-doc-link {
    display: block;
    padding: 0.95rem 1rem;
    border: 1px solid var(--bmx-line);
    background: #fff;
    color: var(--bmx-ink);
    font-family: var(--bmx-mono);
    font-size: 0.74rem;
    line-height: 1.5;
    text-decoration: none;
    transition: border-color 0.2s, background 0.2s, color 0.2s;
  }

  .bmx-legal-doc-link:hover,
  .bmx-legal-doc-link-active {
    border-color: var(--bmx-blue);
    background: var(--bmx-blue);
    color: #fff;
  }

  .bmx-legal-content {
    min-width: 0;
    background: #fff;
    border: 1px solid var(--bmx-line);
  }

  .bmx-legal-content-inner {
    max-width: 980px;
    padding: 3rem;
  }

  .bmx-legal-section-title {
    margin: 2.8rem 0 1rem;
    padding-top: 1.6rem;
    border-top: 1px solid var(--bmx-line);
    color: var(--bmx-ink);
    font-size: 1.5rem;
    line-height: 1.2;
  }

  .bmx-legal-section-title:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  .bmx-legal-paragraph {
    margin: 0 0 1rem;
    color: #3f4b4f;
    font-family: var(--bmx-mono);
    font-size: 0.82rem;
    line-height: 1.9;
  }

  .bmx-legal-table-wrap {
    width: 100%;
    margin: 1.4rem 0 2rem;
    overflow-x: auto;
    border: 1px solid var(--bmx-line);
  }

  .bmx-legal-table {
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    font-family: var(--bmx-mono);
    font-size: 0.76rem;
    line-height: 1.65;
  }

  .bmx-legal-table th,
  .bmx-legal-table td {
    padding: 0.85rem 0.95rem;
    border-right: 1px solid var(--bmx-line);
    border-bottom: 1px solid var(--bmx-line);
    text-align: left;
    vertical-align: top;
  }

  .bmx-legal-table th:last-child,
  .bmx-legal-table td:last-child {
    border-right: none;
  }

  .bmx-legal-table tr:last-child td {
    border-bottom: none;
  }

  .bmx-legal-table th {
    background: var(--bmx-dark);
    color: #fff;
    font-weight: 500;
  }

  .bmx-legal-empty {
    padding: 4rem 3rem;
  }

  .bmx-legal-version {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--bmx-line);
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    letter-spacing: 0.04em;
  }

  .bmx-legal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 2.2rem 3rem;
    background: var(--bmx-dark);
    color: #fff;
  }

  .bmx-legal-footer span {
    color: rgba(255,255,255,0.46);
    font-family: var(--bmx-mono);
    font-size: 0.7rem;
    letter-spacing: 0.06em;
  }

  @media (max-width: 920px) {
    .bmx-legal-nav,
    .bmx-legal-hero,
    .bmx-legal-layout,
    .bmx-legal-footer {
      padding-left: 1.2rem;
      padding-right: 1.2rem;
    }

    .bmx-legal-nav {
      align-items: flex-start;
      flex-direction: column;
    }

    .bmx-legal-nav-actions {
      flex-wrap: wrap;
    }

    .bmx-legal-layout {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .bmx-legal-sidebar {
      position: static;
    }

    .bmx-legal-content-inner {
      padding: 1.4rem;
    }

    .bmx-legal-footer {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

interface RouteParams {
  slug?: string;
}

const isSectionHeading = (block: AKLIOLegalBlock) =>
  block.type === 'paragraph' && /^\d+\.\s+/.test(block.text);

const AKLIOLegalPage: React.FC = () => {
  const { slug } = useParams<RouteParams>();
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const legalDocument = getAKLIOLegalDocument(slug);

  useEffect(() => {
    if (!document.getElementById('aklio-legal-styles')) {
      const el = document.createElement('style');
      el.id = 'aklio-legal-styles';
      el.textContent = styles;
      document.head.appendChild(el);
      styleRef.current = el;
    }

    return () => {
      styleRef.current?.remove();
    };
  }, []);

  const renderBlock = (block: AKLIOLegalBlock, index: number) => {
    if (block.type === 'table') {
      const [header, ...rows] = block.rows;

      return (
        <div className="bmx-legal-table-wrap" key={`table-${index}`}>
          <table className="bmx-legal-table">
            {header && (
              <thead>
                <tr>
                  {header.map((cell, cellIndex) => (
                    <th key={`head-${cellIndex}`}>{cell}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (isSectionHeading(block)) {
      return (
        <h2 className="bmx-legal-section-title" key={`heading-${index}`}>
          {block.text}
        </h2>
      );
    }

    return (
      <p className="bmx-legal-paragraph" key={`paragraph-${index}`}>
        {block.text}
      </p>
    );
  };

  return (
    <div className="bmx-legal-root">
      <nav className="bmx-legal-nav">
        <Link to="/aklio" className="bmx-legal-logo">
          AKLIO
        </Link>

        <div className="bmx-legal-nav-actions">
          <Link to="/aklio" className="bmx-legal-btn bmx-legal-btn-secondary">
            volver a AKLIO
          </Link>
          <Link to="/" className="bmx-legal-btn">
            thebluecoding
          </Link>
        </div>
      </nav>

      {legalDocument ? (
        <>
          <header className="bmx-legal-hero">
            <div className="bmx-legal-eyebrow">documento legal</div>
            <h1 className="bmx-legal-title">{legalDocument.title}</h1>
            <p className="bmx-legal-subtitle">{legalDocument.subtitle}</p>
            <span className="bmx-legal-date">Vigente desde: {legalDocument.effectiveDate}</span>
          </header>

          <main className="bmx-legal-layout">
            <aside className="bmx-legal-sidebar" aria-label="Documentos legales">
              <div className="bmx-legal-sidebar-label">Documentos</div>
              {aklioLegalDocuments.map((item) => (
                <Link
                  key={item.slug}
                  to={`/aklio/legal/${item.slug}`}
                  className={`bmx-legal-doc-link${item.slug === legalDocument.slug ? ' bmx-legal-doc-link-active' : ''}`}
                >
                  {item.navLabel}
                </Link>
              ))}
            </aside>

            <article className="bmx-legal-content">
              <div className="bmx-legal-content-inner">
                <p className="bmx-legal-paragraph">{legalDocument.description}</p>
                {legalDocument.blocks.map(renderBlock)}
                {legalDocument.slug === 'acuerdo-procesamiento-datos' && (
                  <p className="bmx-legal-version">Versión vigente del DPA: AKLIO-DPA-EC-v1.0-2026-10-19</p>
                )}
              </div>
            </article>
          </main>
        </>
      ) : (
        <main className="bmx-legal-empty">
          <h1 className="bmx-legal-title">Documento no encontrado</h1>
          <p className="bmx-legal-subtitle">
            Selecciona uno de los documentos legales disponibles para AKLIO.
          </p>
          <div className="bmx-legal-nav-actions" style={{ marginTop: '1.5rem' }}>
            {aklioLegalDocuments.map((item) => (
              <Link key={item.slug} to={`/aklio/legal/${item.slug}`} className="bmx-legal-btn">
                {item.navLabel}
              </Link>
            ))}
          </div>
        </main>
      )}

      <footer className="bmx-legal-footer">
        <Link to="/aklio" className="bmx-legal-logo">
          AKLIO
        </Link>
        <span>Documentos legales de AKLIO</span>
      </footer>
    </div>
  );
};

export default AKLIOLegalPage;
